import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  History,
  Trash2,
  RotateCcw,
  Tag,
  Mail,
  Lock,
  AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";

/**
 * Icon + color metadata for each audit action. Labels come from i18n
 * (namespace "activity", key `actions.<action>`) so we don't duplicate copy.
 */
const ACTION_META = {
  "document.delete":       { Icon: Trash2,         color: "text-red-700 bg-red-50" },
  "document.restore":      { Icon: RotateCcw,      color: "text-emerald-700 bg-emerald-50" },
  "document.tags_changed": { Icon: Tag,            color: "text-zinc-700 bg-zinc-100" },
  "user.password_reset":   { Icon: Lock,           color: "text-amber-700 bg-amber-50" },
  "email.send":            { Icon: Mail,           color: "text-emerald-700 bg-emerald-50" },
  "email.send_failed":     { Icon: AlertTriangle,  color: "text-red-700 bg-red-50" },
};

/**
 * `describe(entry, t)` — human-readable body line for one audit entry.
 * All strings go through i18next; caller supplies the `t` bound to the
 * "activity" namespace.
 */
function describe(entry, t) {
  const m = entry.metadata || {};
  const unknown = t("describe.unknown_placeholder");
  const empty = t("describe.tags_empty");
  switch (entry.action) {
    case "document.delete":
      return m.filename
        ? t("describe.deleted_named", { filename: m.filename })
        : t("describe.deleted_generic");
    case "document.restore":
      return m.admin_restore
        ? t("describe.restored_admin")
        : t("describe.restored");
    case "document.tags_changed":
      return t("describe.tags_changed", {
        before: (m.before || []).join(", ") || empty,
        after:  (m.after  || []).join(", ") || empty,
      });
    case "user.password_reset":
      return t("describe.password_reset");
    case "email.send":
      return t("describe.email_sent", {
        to: m.to || unknown,
        provider_id: m.provider_id || unknown,
      });
    case "email.send_failed":
      return m.detail || t("describe.email_send_failed_generic");
    default:
      return entry.action;
  }
}

export default function Activity() {
  const { t } = useTranslation("activity");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/audit-log", { params: { limit: 100 } })
      .then((res) => setEntries(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Build a stable action→label lookup so we don't hit i18next once per row.
  const labelFor = useMemo(
    () => (action) => {
      // We rely on i18next's fallback behaviour: if the key doesn't exist,
      // returning the raw action string is a reasonable last-resort label
      // for unknown/future action types (still keeps zero hardcoded copy).
      const key = `actions.${action}`;
      const resolved = t(key);
      return resolved === key ? action : resolved;
    },
    [t],
  );

  return (
    <div className="animate-fade-up" data-testid="activity-page">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
          {t("page_kicker")}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950">
          {t("page_title")}
        </h1>
        <p className="text-sm text-zinc-500 mt-1.5">
          {t("page_subtitle")}
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div
            className="p-10 text-center text-sm text-zinc-400"
            data-testid="activity-loading"
          >
            {t("loading")}
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center" data-testid="activity-empty">
            <div className="w-12 h-12 mx-auto rounded-xl bg-zinc-100 grid place-items-center mb-4">
              <History size={20} className="text-zinc-500" />
            </div>
            <p className="font-display text-base font-semibold text-zinc-900 mb-1">
              {t("empty_title")}
            </p>
            <p className="text-sm text-zinc-500">
              {t("empty_subtitle")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100" data-testid="activity-list">
            {entries.map((e) => {
              const meta = ACTION_META[e.action] || {
                Icon: History,
                color: "text-zinc-700 bg-zinc-100",
              };
              const Icon = meta.Icon;
              return (
                <li
                  key={e.id}
                  data-testid={`activity-row-${e.action}`}
                  className="px-5 py-4 flex items-start gap-3"
                >
                  <div className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${meta.color}`}>
                    <Icon size={14} strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-900">
                      {labelFor(e.action)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5 break-words">
                      {describe(e, t)}
                    </p>
                  </div>
                  <span className="text-[11px] text-zinc-400 shrink-0 mt-0.5">
                    {new Date(e.ts).toLocaleString()}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
